import { useLayoutEffect, useRef, type RefObject } from 'react';

/** How long layout shifts (content-visibility cards resolving their real height) are re-corrected. */
const SETTLE_MS = 1_500;
/** Any of these on the page means the user took over scrolling — stop correcting. */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

/** Nearest ancestor that scrolls vertically, or null when the page (window) scrolls. */
function findScrollParent(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Scrolls the nearest vertical scroll container of `containerRef` (a slide's
 * `.page-scroll` on mobile, the window on desktop) so the descendant marked
 * `data-anchor-key={anchorKey}` sits right below the sticky `barRef` toolbar
 * (whose own bottom padding is the gap).
 *
 * Runs once per `trigger` value (e.g. the view mode) as soon as `anchorKey`
 * resolves — data refetches never re-anchor. Only the vertical axis is touched,
 * so an off-screen swipe slide is never shifted sideways.
 *
 * `content-visibility: auto` cards above the target resolve their real height
 * a few frames later; a ResizeObserver on the container re-aligns on every
 * such shift for `SETTLE_MS`, until the user starts scrolling themselves.
 */
export function useScrollAnchor(
  containerRef: RefObject<HTMLElement>,
  barRef: RefObject<HTMLElement>,
  anchorKey: string | null,
  trigger: string,
): void {
  const anchoredTriggerRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!anchorKey || !container || anchoredTriggerRef.current === trigger) return undefined;
    anchoredTriggerRef.current = trigger;

    const scroller = findScrollParent(container);
    const align = () => {
      const target = container.querySelector<HTMLElement>(
        `[data-anchor-key="${CSS.escape(anchorKey)}"]`,
      );
      if (!target) return;
      const offset = barRef.current?.getBoundingClientRect().height ?? 0;
      const viewportTop = scroller ? scroller.getBoundingClientRect().top : 0;
      const delta = target.getBoundingClientRect().top - viewportTop - offset;
      if (Math.abs(delta) < 1) return;
      if (scroller) scroller.scrollTop += delta;
      else window.scrollBy(0, delta);
    };

    align();

    let settling = true;
    const observer = new ResizeObserver(align);
    const stop = () => {
      settling = false;
      observer.disconnect();
      window.clearTimeout(timer);
      USER_SCROLL_EVENTS.forEach(type => window.removeEventListener(type, stop, true));
    };
    const timer = window.setTimeout(stop, SETTLE_MS);
    observer.observe(container);
    USER_SCROLL_EVENTS.forEach(type =>
      window.addEventListener(type, stop, { capture: true, passive: true }));

    return () => {
      // Torn down mid-settle (StrictMode's effect replay, or new deps): let the
      // next run anchor again — anchoring is idempotent.
      if (settling) anchoredTriggerRef.current = null;
      stop();
    };
  }, [anchorKey, barRef, containerRef, trigger]);
}
